/**
 * ██╗  ██╗ ██████╗ ██████╗  ██████╗
 * ██║ ██╔╝██╔═══██╗██╔══██╗██╔═══██╗
 * █████╔╝ ██║   ██║██████╔╝██║   ██║
 * ██╔═██╗ ██║   ██║██╔══██╗██║   ██║
 * ██║  ██╗╚██████╔╝██████╔╝╚██████╔╝
 * ╚═╝  ╚═╝ ╚═════╝ ╚═════╝  ╚═════╝
 *
 * (c) Copyright 2021-present Rakuten Kobo Inc. (https://www.kobo.com)
 */

// -----------------------------------------------------------------------------
// IMPORTS
// -----------------------------------------------------------------------------

// External/Third-party dependencies
import { useEffect, useRef } from "react";

// Internal dependencies
import useStyleSystem from "./useStyleSystem";


// -----------------------------------------------------------------------------
// STYLES
// -----------------------------------------------------------------------------

function createStyles({ theme, props }) {
  const { colors, variables } = theme;

  const rippleColor = props.rippleColor || colors.background;

  return {
    ripple: {
      position: "absolute",
      borderRadius: "50%",
      height: "32%",
      width: "32%",
      left: "50%",
      top: "50%",
      transform: "translate(-50%, -50%) scale(0)",
      animation: `ripple ${variables.animationRippleDuration} ${variables.animationRippleEasing}`,
      pointerEvents: "none",
      backgroundColor: rippleColor,
      outlineColor: rippleColor,
      zIndex: 0
    }
  };
}

// -----------------------------------------------------------------------------
// RIPPLE EFFECT HOOK
// -----------------------------------------------------------------------------

/**
 * While the :active pseudo-class does indeed work with keyboard events,
 * the ripple effect might not appear as expected because:
 *
 * The `:active` state is triggered with keyboard events on a focusable element like a <button>.
 *
 * The :active state is transient—it only lasts while the button is being "activated"
 * (e.g., while the Space key is held down).
 * If the ripple animation is not triggered quickly enough, it might not be visible.
 *
 * Besides that, we encountered an issue where clicking would trigger a double ripple.
 * Therefore, we made the decision to handle this animation via JS.
 *
 * @param {Object} options - The options for the ripple effect.
 * @param {boolean} [options.disabled=false] - Whether the ripple effect is disabled.
 * @param {string} [options.rippleColor] - The color of the ripple effect (e.g., "rgba(0, 0, 0, 0.3)").
 * @returns {{ buttonRef: React.RefObject<HTMLElement> }}
 */
export default function useRippleEffect({ disabled, rippleColor } = { disabled: false }) {
  const buttonRef = useRef();

  // "Hook" this component into the theme/style system
  const [styles] = useStyleSystem(
    {
      id: "RippleEffect",
      props: {
        rippleColor
      },
      styleFn: createStyles
    },
    []
  );

  /**
   * Creates a new ripple element and attaches it to the
   * button specified with the ref.
   * Removes the element when the animation is finished.
   *
   * @param {MouseEvent} e
   */
  function createRippleEffect(e) {
    const rippleElement = document.createElement("span");
    const button = buttonRef.current || e.currentTarget;

    rippleElement.classList.add(styles.ripple);
    button.appendChild(rippleElement);

    rippleElement.addEventListener("animationend", () => {
      rippleElement.remove();
    });
  }


  /**
   * Expose a method to trigger the ripple effect programmatically
   */
  const triggerRipple = (event) => {
    if(buttonRef.current) {
      createRippleEffect(event);
    }
  }


  /**
   * Trigger the ripple effect when key press is only
   * space or Enter
   *
   * @param {KeyboardEvent} e
   */
  function handleKeyPress(e) {
    if (e.key === "Enter" || e.key === " ") {
      createRippleEffect(e);
    }
  }

  /**
   * Remove `onPressed` event listeners that trigger
   * the ripple effect.
   */
  const cleanUpEventListeners = () => {
    const button = buttonRef.current;

    button?.removeEventListener("click", createRippleEffect);
    button?.removeEventListener("keydown", handleKeyPress);
  };

  /**
   * Add an event listener to the button reference and listen
   * for `click` or for `space - enter`.
   */
  useEffect(() => {
    const button = buttonRef.current;

    if (!button) {
      return;
    }

    if (!disabled) {
      button.addEventListener("click", createRippleEffect);
      button.addEventListener("keydown", handleKeyPress);
    }

    return () => {
      cleanUpEventListeners();
    };
  }, [createRippleEffect, handleKeyPress, disabled]);

  /**
   * Remove event listeners if the button has been disabled
   */
  useEffect(() => {
    if (disabled) {
      cleanUpEventListeners();
    }
  }, [disabled]);

  return {
    buttonRef,
    triggerRipple
  };
}
